import { useMutation, useQuery } from '@tanstack/react-query';

import { env } from '@/constants/env';
import { customFetch } from '@/lib/fetch-interceptor';
import type { MutationConfig, QueryConfig } from '@/lib/react-query';
import { api, extractData } from '@/lib/treaty';

export type ShadowmapRequestRow = {
  id: string;
  username?: string | null;
  userId: string | null;
  tenantId: string | null;
  estateId: string | null;
  projectId: string | null;
  address: string;
  source: 'connect' | 'mobile';
  sharedLink: string | null;
  createdAt: string;
};

export type ShadowmapRequestFilters = {
  userId?: string;
  tenantId?: string;
  estateId?: string;
  projectId?: string;
  source?: 'connect' | 'mobile';
  from?: string;
  to?: string;
  q?: string;
};

export type Paginated<T> = {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
};

export const getShadowmapRequests = async ({
  page = 1,
  pageSize = 50,
  filters = {},
}: {
  page?: number;
  pageSize?: number;
  filters?: ShadowmapRequestFilters;
}) => {
  const query: Record<string, string> = {
    page: String(page),
    pageSize: String(pageSize),
  };
  if (filters.userId) query.userId = filters.userId;
  if (filters.tenantId) query.tenantId = filters.tenantId;
  if (filters.estateId) query.estateId = filters.estateId;
  if (filters.projectId) query.projectId = filters.projectId;
  if (filters.source) query.source = filters.source;
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  if (filters.q) query.q = filters.q;

  return extractData(
    api.shadowmaps.requests.get({ query }) as unknown as Parameters<typeof extractData>[0]
  ) as Promise<Paginated<ShadowmapRequestRow>>;
};

export const getShadowmapRequestsOptions = (
  page: number,
  pageSize: number,
  filters: ShadowmapRequestFilters
) => ({
  queryKey: ['shadowmap-requests', page, pageSize, filters],
  queryFn: () => getShadowmapRequests({ page, pageSize, filters }),
});

export const useShadowmapRequests = ({
  page = 1,
  pageSize = 20,
  filters = {},
  queryConfig,
}: {
  page?: number;
  pageSize?: number;
  filters?: ShadowmapRequestFilters;
  queryConfig?: QueryConfig<typeof getShadowmapRequests>;
} = {}) => {
  return useQuery({
    ...getShadowmapRequestsOptions(page, pageSize, filters),
    enabled: Boolean(filters.tenantId) && (queryConfig?.enabled ?? true),
    ...queryConfig,
  });
};

export const exportShadowmapRequestsCsv = async (filters: ShadowmapRequestFilters = {}) => {
  const url = new URL(`${env.API_URL}/shadowmaps/requests`);
  url.searchParams.set('format', 'csv');
  if (!filters.tenantId) {
    throw new Error('tenantId is required to export shadowmap requests');
  }
  if (filters.userId) url.searchParams.set('userId', filters.userId);
  url.searchParams.set('tenantId', filters.tenantId);
  if (filters.estateId) url.searchParams.set('estateId', filters.estateId);
  if (filters.projectId) url.searchParams.set('projectId', filters.projectId);
  if (filters.source) url.searchParams.set('source', filters.source);
  if (filters.from) url.searchParams.set('from', filters.from);
  if (filters.to) url.searchParams.set('to', filters.to);
  if (filters.q) url.searchParams.set('q', filters.q);

  const res = await customFetch(url.toString());
  if (!res.ok) {
    throw new Error('Failed to export CSV');
  }
  return await res.blob();
};

export const useExportShadowmapRequestsCsv = ({
  mutationConfig,
}: {
  mutationConfig?: MutationConfig<typeof exportShadowmapRequestsCsv>;
} = {}) => {
  return useMutation({ ...mutationConfig, mutationFn: exportShadowmapRequestsCsv });
};

export const trackShadowmapRequest = async (data: {
  address: string;
  source: 'connect' | 'mobile';
  tenantId: string;
  shadowmapId?: string;
}) => {
  return extractData(
    api.shadowmaps.track.post(data) as unknown as Parameters<typeof extractData>[0]
  );
};

export const useTrackShadowmap = ({
  mutationConfig,
}: {
  mutationConfig?: MutationConfig<typeof trackShadowmapRequest>;
} = {}) => {
  return useMutation({ ...mutationConfig, mutationFn: trackShadowmapRequest });
};
